import { useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  DollarSign,
  FileText,
  Images,
  Camera,
  CalendarDays,
  MessageSquare,
  RotateCcw,
  Send,
  SlidersHorizontal,
  Sparkles,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { api, type CarouselCatalogFormat, type CarouselChangeProposal, type CarouselLesson, type CarouselPromptSetting } from "@/lib/api";
import {
  CONCEPT_KEYS,
  CONCEPT_PROMPT_KEYS,
  COPY_MODEL_KEYS,
  COPY_PROMPT_KEYS,
  FALLBACK_TYPES,
  DESIGN_PROMPT_KEYS,
  IMAGE_KEYS,
  PHOTO_KEYS,
  WEEK_KEYS,
  MEMORY_KEYS,
  OVERVIEW_CARDS,
  PIPELINE_CHIPS,
  PRICE_KIND_LABEL,
  RETRO_MODEL_KEYS,
  RETRO_PROMPT_KEYS,
  ROLE_MODEL_KEYS,
  ROLE_PROMPT_KEYS,
  TABS,
  TONE_CLASS,
  fieldMeta,
  groupPricing,
  lessonKindLabel,
  lessonStatusLabel,
  sourceLabel,
  tabById,
  type SettingsTab,
  type SettingsTone,
  type SettingsIconName,
} from "@/lib/carouselSettingsMeta";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InfoTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const ICONS: Record<SettingsIconName, LucideIcon> = {
  Workflow,
  Sparkles,
  MessageSquare,
  FileText,
  Images,
  Camera,
  CalendarDays,
  RotateCcw,
  DollarSign,
  SlidersHorizontal,
  Send,
};

export default function KaruzeleUstawienia() {
  const qc = useQueryClient();
  const promptsQ = useQuery({ queryKey: ["carousel-prompts"], queryFn: api.carouselPrompts });
  const catalogQ = useQuery({ queryKey: ["carousel-catalog"], queryFn: api.carouselCatalog });
  const lessonsQ = useQuery({ queryKey: ["carousel-lessons"], queryFn: () => api.carouselLessons() });
  const changesQ = useQuery({ queryKey: ["carousel-changes"], queryFn: () => api.carouselChanges() });
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [manualText, setManualText] = useState("");
  const [manualKind, setManualKind] = useState("watch");
  const [tab, setTab] = useState<SettingsTab>("overview");
  const [openPrompt, setOpenPrompt] = useState<string | null>(null);

  const items = promptsQ.data?.items || [];
  const byKey = new Map(items.map((row) => [row.key, row]));
  const valueOf = (key: string, fallback = "") =>
    draft[key] !== undefined ? draft[key] : byKey.get(key)?.value || fallback;
  const dirty = Object.keys(draft).length;
  const catalogTypes = (() => {
    const raw = catalogQ.data?.types || catalogQ.data?.formats || [];
    return raw.length ? raw : FALLBACK_TYPES;
  })();
  const pricingKeys = items.filter((row) => row.key.startsWith("pricing.")).map((row) => row.key);

  const saveM = useMutation({
    mutationFn: () => api.carouselPromptsPut(draft),
    onSuccess: () => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ["carousel-prompts"] });
    },
  });
  const restoreM = useMutation({
    mutationFn: (keys?: string[]) => api.carouselPromptsRestore(keys),
    onSuccess: () => {
      setDraft({});
      qc.invalidateQueries({ queryKey: ["carousel-prompts"] });
    },
  });
  const lessonM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "approved" | "rejected" }) =>
      api.carouselLessonPatch(id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel-lessons"] }),
  });
  const changeM = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approved" | "rejected" }) =>
      action === "approved" ? api.carouselChangeApprove(id) : api.carouselChangeReject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel-changes"] });
      qc.invalidateQueries({ queryKey: ["carousel-prompts"] });
    },
  });
  const aggregateM = useMutation({
    mutationFn: () => api.carouselChangesFromRetros([], 5),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel-changes"] }),
  });
  const addLessonM = useMutation({
    mutationFn: () => api.carouselLessonCreate({ text: manualText, kind: manualKind }),
    onSuccess: () => {
      setManualText("");
      qc.invalidateQueries({ queryKey: ["carousel-lessons"] });
    },
  });

  const goTab = (next: SettingsTab) => {
    setTab(next);
    setOpenPrompt(null);
  };

  const fieldProps = { byKey, valueOf, onChange: setDraft };

  return (
    <div className="pb-24">
      <PageHeader
        title="Ustawienia kreatora"
        subtitle="Pogrupowane po krokach studia. Klucze techniczne zostają pod etykietą."
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/karuzele">
              <ArrowLeft className="mr-2 h-3.5 w-3.5" />
              Studio
            </Link>
          </Button>
        }
      />

      <ProcessStrip activeTab={tab} onPick={goTab} />

      <Tabs value={tab} onValueChange={(value) => goTab(value as SettingsTab)}>
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1">
          {TABS.map((row) => {
            const Icon = ICONS[row.icon];
            return (
              <TabsTrigger key={row.id} value={row.id} className="gap-1.5">
                <Icon className={cn("h-3.5 w-3.5", TONE_CLASS[row.tone].icon)} />
                {row.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {promptsQ.isLoading && (
          <p className="mb-3 text-sm text-muted-foreground">Ładuję ustawienia…</p>
        )}
        <TabBanner tab={tab} />

        <TabsContent value="overview">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {OVERVIEW_CARDS.map((card) => (
              <button
                key={card.step + card.title}
                type="button"
                onClick={() => goTab(card.tab)}
                className={cn(
                  "rounded-2xl border border-border/60 bg-card p-4 text-left transition-colors hover:bg-accent/40",
                  "border-l-4",
                  TONE_CLASS[card.tone].bar,
                )}
              >
                <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className={cn("rounded-full px-2 py-0.5", TONE_CLASS[card.tone].chip)}>krok {card.step}</span>
                  <span>otwórz zakładkę</span>
                </div>
                <div className="text-sm font-medium text-foreground">{card.title}</div>
                <p className="mt-1 text-xs text-muted-foreground">{card.extra}</p>
                {card.modelKeys.length > 0 && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Modele: {card.modelKeys.map((key) => fieldMeta(key).label).join(", ")}
                  </p>
                )}
                {card.promptKeys.length > 0 && (
                  <p className="text-[11px] text-muted-foreground">Prompty: {card.promptKeys.join(", ")}</p>
                )}
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="concepts" className="space-y-4">
          <FieldGrid keys={[...CONCEPT_KEYS]} {...fieldProps} />
          <PromptStack
            keys={[...CONCEPT_PROMPT_KEYS]}
            byKey={byKey}
            valueOf={valueOf}
            onChange={setDraft}
            openKey={openPrompt}
            onOpen={setOpenPrompt}
          />
        </TabsContent>

        <TabsContent value="roles" className="space-y-4">
          <FieldGrid keys={[...ROLE_MODEL_KEYS]} {...fieldProps} />
          <SectionCard tone="primary" title="Co wraca z retro">
            <p className="mb-3 text-xs text-muted-foreground">
              Pamięć dokleja się tylko do panelu ról, nie do koncepcji ani obrazu.
            </p>
            <FieldGrid keys={[...MEMORY_KEYS]} {...fieldProps} compact />
          </SectionCard>
          <PromptStack
            keys={[...ROLE_PROMPT_KEYS]}
            byKey={byKey}
            valueOf={valueOf}
            onChange={setDraft}
            openKey={openPrompt}
            onOpen={setOpenPrompt}
          />
        </TabsContent>

        <TabsContent value="copy" className="space-y-4">
          {(promptsQ.data?.placeholders || []).length > 0 && (
            <p className="text-xs text-muted-foreground">
              Placeholdery w promptach: {(promptsQ.data?.placeholders || []).join(" ")}
            </p>
          )}
          <FieldGrid keys={[...COPY_MODEL_KEYS]} {...fieldProps} />
          <PromptStack
            keys={[...COPY_PROMPT_KEYS]}
            byKey={byKey}
            valueOf={valueOf}
            onChange={setDraft}
            openKey={openPrompt}
            onOpen={setOpenPrompt}
          />
          <SectionCard tone="success" title="Prompty typów">
            <p className="mb-3 text-xs text-muted-foreground">
              Wszystkie otwarte obok siebie. Reveal (ostatni slajd = odpowiedź) tylko w story. Przywróć resetuje wyłącznie ten typ.
            </p>
            <TypePromptColumns
              types={catalogTypes}
              byKey={byKey}
              valueOf={valueOf}
              onChange={setDraft}
              onRestore={(key) => restoreM.mutate([key])}
              restorePending={restoreM.isPending}
            />
          </SectionCard>
        </TabsContent>

        <TabsContent value="image" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Render woła Gemini raz na slajd. Lite ~4 s, Flash dłużej, Pro najwolniejszy. Zapas tylko gdy główny padnie.
          </p>
          <FieldGrid keys={[...IMAGE_KEYS]} {...fieldProps} />
          <PromptStack
            keys={[...DESIGN_PROMPT_KEYS]}
            byKey={byKey}
            valueOf={valueOf}
            onChange={setDraft}
            openKey={openPrompt}
            onOpen={setOpenPrompt}
          />
        </TabsContent>

        <TabsContent value="photos" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Inbox <code>inbox/zdjecia</code> albo upload na /zdjecia (można wiele plików naraz). Po tagowaniu klikasz Do biblioteki.
          </p>
          <FieldGrid keys={[...PHOTO_KEYS]} {...fieldProps} />
        </TabsContent>

        <TabsContent value="week" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Plan tygodnia liczy sloty wobec tych procentów. Szablon dni jest w week.slots.json (JSON).
          </p>
          <FieldGrid keys={[...WEEK_KEYS]} {...fieldProps} />
        </TabsContent>

        <TabsContent value="retro" className="space-y-4">
          <FieldGrid keys={[...RETRO_MODEL_KEYS]} {...fieldProps} />
          <PromptStack
            keys={[...RETRO_PROMPT_KEYS]}
            byKey={byKey}
            valueOf={valueOf}
            onChange={setDraft}
            openKey={openPrompt}
            onOpen={setOpenPrompt}
          />
          <SectionCard tone="muted" title="Propozycje zmian">
            <div className="mb-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" disabled={aggregateM.isPending} onClick={() => aggregateM.mutate()}>
                Suma ostatnich retro
              </Button>
            </div>
            <ProposalList
              items={changesQ.data?.items || []}
              onStatus={(id, status) => changeM.mutate({ id, action: status })}
            />
          </SectionCard>
          <SectionCard tone="muted" title="Wnioski">
            <div className="mb-3 flex flex-wrap gap-2">
              <Input
                placeholder="ręczny wniosek"
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
              />
              <Select
                aria-label="Rodzaj wniosku"
                value={manualKind}
                onChange={setManualKind}
                options={[
                  { value: "repeat", label: "powtarzaj" },
                  { value: "avoid", label: "unikaj" },
                  { value: "watch", label: "obserwuj" },
                ]}
              />
              <Button size="sm" disabled={!manualText.trim() || addLessonM.isPending} onClick={() => addLessonM.mutate()}>
                Dodaj do kolejki
              </Button>
            </div>
            <LessonList items={lessonsQ.data?.items || []} onStatus={(id, status) => lessonM.mutate({ id, status })} />
          </SectionCard>
        </TabsContent>

        <TabsContent value="pricing">
          <div className="grid gap-3 md:grid-cols-2">
            {groupPricing(pricingKeys).map((group) => (
              <SectionCard key={group.model} tone="secondary" title={group.model}>
                <div className="grid gap-3">
                  {group.items.map((item) => (
                    <FieldRow
                      key={item.key}
                      fieldKey={item.key}
                      label={PRICE_KIND_LABEL[item.kind] || item.kind}
                      hint="USD. Trafia do stopki kosztu w studiu."
                      tip="Nie zmienia modelu. Zmienia tylko licznik w stopce."
                      {...fieldProps}
                    />
                  ))}
                </div>
              </SectionCard>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {dirty > 0 && (
        <div className="fixed bottom-0 left-64 right-0 z-40 border-t border-border bg-card/95 px-8 py-3 shadow-lg backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground">
              {dirty} {dirty === 1 ? "niezapisana zmiana" : "niezapisane zmiany"}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => restoreM.mutate(undefined)} disabled={restoreM.isPending}>
                <RotateCcw className="mr-2 h-3.5 w-3.5" />
                Przywróć wszystkie domyślne
              </Button>
              <Button size="sm" variant="brand" onClick={() => saveM.mutate()} disabled={saveM.isPending}>
                Zapisz
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProcessStrip({
  activeTab,
  onPick,
}: {
  activeTab: SettingsTab;
  onPick: (tab: SettingsTab) => void;
}) {
  return (
    <ol className="mb-5 grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
      {PIPELINE_CHIPS.map((chip, index) => {
        const Icon = ICONS[chip.icon];
        const active = chip.tab === activeTab && chip.tab !== "overview";
        const overviewActive = activeTab === "overview" && (chip.id === "params" || chip.id === "publish");
        return (
          <li key={chip.id}>
            <button
              type="button"
              onClick={() => onPick(chip.tab)}
              className={cn(
                "flex h-full w-full items-start gap-2 rounded-xl border border-border/60 bg-card p-3 text-left",
                "border-l-4",
                TONE_CLASS[chip.tone].bar,
                (active || overviewActive) && "ring-2 ring-ring",
              )}
            >
              <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full", TONE_CLASS[chip.tone].head)}>
                <Icon className={cn("h-3.5 w-3.5", TONE_CLASS[chip.tone].icon)} />
              </span>
              <span>
                <span className="block text-[11px] text-muted-foreground">{index + 1}</span>
                <span className="block text-sm font-medium text-foreground">{chip.label}</span>
                <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{chip.uses}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function TabBanner({ tab }: { tab: SettingsTab }) {
  const meta = tabById(tab);
  const Icon = ICONS[meta.icon];
  return (
    <div className={cn("mb-4 flex items-start gap-3 rounded-xl border border-border/60 p-3", TONE_CLASS[meta.tone].head)}>
      <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASS[meta.tone].icon)} />
      <p className="text-sm text-foreground">{meta.blurb}</p>
    </div>
  );
}

function SectionCard({
  title,
  tone,
  children,
}: {
  title: string;
  tone: SettingsTone;
  children: ReactNode;
}) {
  return (
    <Card className={cn("border-l-4", TONE_CLASS[tone].bar)}>
      <CardHeader className={cn("rounded-t-2xl", TONE_CLASS[tone].head)}>
        <CardTitle className="text-sm font-medium text-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function FieldGrid({
  keys,
  byKey,
  valueOf,
  onChange,
  compact,
}: {
  keys: string[];
  byKey: Map<string, CarouselPromptSetting>;
  valueOf: (key: string) => string;
  onChange: Dispatch<SetStateAction<Record<string, string>>>;
  compact?: boolean;
}) {
  return (
    <div className={cn("grid gap-3", compact && "md:grid-cols-2")}>
      {keys.map((key) => (
        <FieldRow key={key} fieldKey={key} byKey={byKey} valueOf={valueOf} onChange={onChange} />
      ))}
    </div>
  );
}

function FieldRow({
  fieldKey,
  byKey,
  valueOf,
  onChange,
  label,
  hint,
  tip,
}: {
  fieldKey: string;
  byKey: Map<string, CarouselPromptSetting>;
  valueOf: (key: string) => string;
  onChange: Dispatch<SetStateAction<Record<string, string>>>;
  label?: string;
  hint?: string;
  tip?: string;
}) {
  const meta = fieldMeta(fieldKey);
  const row = byKey.get(fieldKey);
  const shownLabel = label || meta.label;
  const shownHint = hint ?? meta.hint;
  const shownTip = tip || meta.tip;
  const current = valueOf(fieldKey);
  const selectOptions =
    meta.kind === "select" && meta.options
      ? meta.options.some((opt) => opt.value === current)
        ? meta.options
        : current
          ? [...meta.options, { value: current, label: `${current} (zapisany)` }]
          : meta.options
      : meta.options;
  return (
    <label className="block rounded-lg border border-border/60 bg-card/40 p-3">
      <span className="mb-1 flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
        {shownLabel}
        <InfoTip text={shownTip} />
        {row?.source && (
          <Badge variant={row.source === "db" ? "brand" : "outline"}>{sourceLabel(row.source)}</Badge>
        )}
      </span>
      {shownHint && <p className="mb-2 text-xs text-muted-foreground">{shownHint}</p>}
      {meta.kind === "select" && selectOptions ? (
        <Select
          aria-label={shownLabel}
          value={current}
          onChange={(value) => onChange((prev) => ({ ...prev, [fieldKey]: value }))}
          options={selectOptions}
        />
      ) : (
        <Input
          value={valueOf(fieldKey)}
          onChange={(e) => onChange((prev) => ({ ...prev, [fieldKey]: e.target.value }))}
        />
      )}
      <span className="mt-1 block font-mono text-[10px] text-muted-foreground">{fieldKey}</span>
    </label>
  );
}

function TypePromptColumns({
  types,
  byKey,
  valueOf,
  onChange,
  onRestore,
  restorePending,
}: {
  types: CarouselCatalogFormat[];
  byKey: Map<string, CarouselPromptSetting>;
  valueOf: (key: string) => string;
  onChange: Dispatch<SetStateAction<Record<string, string>>>;
  onRestore: (key: string) => void;
  restorePending?: boolean;
}) {
  if (!types.length) {
    return <p className="text-sm text-muted-foreground">Brak typów w katalogu.</p>;
  }
  return (
    <div className="-mx-1 overflow-x-auto pb-2">
      <div className="flex min-w-full gap-3">
        {types.map((fmt) => {
          const key = fmt.prompt_key || `prompt.type.${fmt.id}`;
          const meta = fieldMeta(key);
          const row = byKey.get(key) || { key, value: "", source: "file" };
          const tip =
            fmt.close === "reveal"
              ? "Ostatni slajd to odpowiedź na hak, nie karta STOP."
              : "Ostatni slajd to narzędzie albo STOP, nie historia.";
          return (
            <section
              key={key}
              className="flex w-[min(22rem,80vw)] shrink-0 flex-col rounded-xl border border-border/60 bg-card"
            >
              <header className="flex items-start gap-2 border-b border-border/60 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <span className="truncate">{fmt.label}</span>
                    <InfoTip text={tip} />
                  </div>
                  <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                    {fmt.id} · close {fmt.close || "brak"}
                  </p>
                </div>
                <Badge variant={row.source === "db" ? "brand" : "outline"}>{sourceLabel(row.source)}</Badge>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 shrink-0 px-2"
                  disabled={restorePending}
                  onClick={() => onRestore(key)}
                >
                  <RotateCcw className="h-3 w-3" />
                  <span className="sr-only">Przywróć {fmt.label}</span>
                </Button>
              </header>
              <textarea
                aria-label={fmt.label}
                className="min-h-[28rem] flex-1 resize-y rounded-b-xl border-0 bg-transparent p-3 text-sm leading-relaxed"
                value={valueOf(key)}
                onChange={(e) => onChange((prev) => ({ ...prev, [key]: e.target.value }))}
              />
              {meta.hint && (
                <p className="border-t border-border/60 px-3 py-1.5 text-[11px] text-muted-foreground">{meta.hint}</p>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function PromptStack({
  keys,
  labels,
  tips,
  byKey,
  valueOf,
  onChange,
  openKey,
  onOpen,
  onRestore,
  restorePending,
}: {
  keys: string[];
  labels?: Record<string, string>;
  tips?: Record<string, string>;
  byKey: Map<string, CarouselPromptSetting>;
  valueOf: (key: string) => string;
  onChange: Dispatch<SetStateAction<Record<string, string>>>;
  openKey: string | null;
  onOpen: (key: string | null) => void;
  onRestore?: (key: string) => void;
  restorePending?: boolean;
}) {
  return (
    <div className="space-y-2">
      {keys.map((key) => {
        const meta = fieldMeta(key);
        const row = byKey.get(key) || { key, value: "", source: "file" };
        const label = labels?.[key] || meta.label;
        return (
          <details
            key={key}
            open={openKey === key}
            onToggle={(e) => {
              const next = (e.currentTarget as HTMLDetailsElement).open;
              if (next) onOpen(key);
              else if (openKey === key) onOpen(null);
            }}
            className="rounded-xl border border-border/60 bg-card"
          >
            <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-sm font-medium text-foreground">
              <span className="flex-1">{label}</span>
              <span
                onClick={(e) => e.preventDefault()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <InfoTip text={tips?.[key] || meta.tip} />
              </span>
              <Badge variant={row.source === "db" ? "brand" : "outline"}>{sourceLabel(row.source)}</Badge>
              {onRestore ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  disabled={restorePending}
                  onClick={(e) => {
                    e.preventDefault();
                    onRestore(key);
                  }}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Przywróć ten
                </Button>
              ) : null}
            </summary>
            <div className="border-t border-border/60 p-3">
              {meta.hint && <p className="mb-2 text-xs text-muted-foreground">{meta.hint}</p>}
              <textarea
                className="min-h-[160px] w-full rounded-md border border-input bg-transparent p-2 text-sm"
                value={valueOf(key)}
                onChange={(e) => onChange((prev) => ({ ...prev, [key]: e.target.value }))}
              />
              <span className="mt-1 block font-mono text-[10px] text-muted-foreground">{key}</span>
            </div>
          </details>
        );
      })}
    </div>
  );
}

function ProposalList({
  items,
  onStatus,
}: {
  items: CarouselChangeProposal[];
  onStatus: (id: string, status: "approved" | "rejected") => void;
}) {
  if (!items.length) {
    return <p className="text-sm text-muted-foreground">Brak propozycji. Retro albo suma retro je doda.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((row) => (
        <div key={row.id} className="rounded-md border border-border p-2 text-sm">
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant={row.status === "approved" ? "success" : "outline"}>{row.status || "pending"}</Badge>
            <Badge variant="outline">{row.source || "retro"}</Badge>
            <span className="font-mono">{row.target}</span>
          </div>
          {row.why ? <p className="mt-1">{row.why}</p> : null}
          {row.patch?.value ? (
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted/40 p-2 text-[11px]">
              {row.patch.value}
            </pre>
          ) : null}
          {row.status === "pending" && (
            <div className="mt-2 flex gap-1">
              <Button size="sm" variant="outline" onClick={() => onStatus(row.id, "approved")}>
                Zatwierdź
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onStatus(row.id, "rejected")}>
                Odrzuć
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LessonList({
  items,
  onStatus,
}: {
  items: CarouselLesson[];
  onStatus: (id: string, status: "approved" | "rejected") => void;
}) {
  if (!items.length) {
    return <p className="text-sm text-muted-foreground">Brak wniosków.</p>;
  }
  return (
    <div className="space-y-2">
      {items.map((les) => (
        <div key={les.id} className="flex items-start justify-between gap-3 rounded-md border border-border p-2 text-sm">
          <div>
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              <Badge variant={les.status === "approved" ? "success" : "outline"}>{lessonStatusLabel(les.status)}</Badge>
              <Badge variant="outline">{lessonKindLabel(les.kind)}</Badge>
            </div>
            <p className="mt-1">{les.text}</p>
          </div>
          {les.status === "pending" && (
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="outline" onClick={() => onStatus(les.id, "approved")}>
                Zatwierdź
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onStatus(les.id, "rejected")}>
                Odrzuć
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
